import {
  faSquare as faSquareRegular,
  faCircle as faCircleRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCheck,
  faSquare as faSquareSolid,
  faCircle as faCircleSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { HTMLAttributes, ReactNode, useMemo } from 'react';
import VoxelText from '../Text/VoxelText';

export interface VoxelCheckboxProps extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  label?: string | ReactNode;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  color?: CheckboxColor;
  shape?: 'circle' | 'square';
  padding?: boolean;
  widthClass?: string;
  errorText?: string;
  size?: 'sm' | 'md';
}

export type CheckboxColor =
  | 'primary'
  | 'outlined'
  | 'indigo'
  | 'emerald'
  | 'gray'
  | 'slate'
  ;

export const VoxelCheckbox = React.forwardRef<HTMLLabelElement, VoxelCheckboxProps>(
  (
    {
      label,
      disabled,
      checked = false,
      id,
      onChange,
      color = 'outlined',
      padding,
      widthClass,
      shape = 'square',
      errorText,
      size = 'md',
      ...labelProps
    },
    ref
  ) => {
    const finalId = useMemo(() => id ?? Math.random().toString().slice(8), [id]);

    let boxIcon = faSquareRegular;
    let boxColor = 'text-voxel-gray-200';

    let checkIcon = faCheck;
    let checkColor = 'text-voxel-gray-200';

    let hoverColor = 'group-hover:bg-voxel-gray-100/75';

    // Shape
    if (shape === 'square' && !checked) {
      boxIcon = faSquareRegular;
    }
    if (shape === 'square' && checked && color === 'outlined') {
      boxIcon = faSquareRegular;
    }
    if (shape === 'square' && checked && color !== 'outlined') {
      boxIcon = faSquareSolid;
    }
    if (shape === 'circle' && !checked) {
      boxIcon = faCircleRegular;
    }
    if (shape === 'circle' && checked && color === 'outlined') {
      boxIcon = faCircleRegular;
    }
    if (shape === 'circle' && checked && color !== 'outlined') {
      boxIcon = faCircleSolid;
    }

    // COLORING

    // Unchecked states
    if (!checked) {
      boxColor = 'text-gray-500';
    }

    if (disabled) {
      boxColor = 'text-voxel-gray-300';
      checkColor = 'text-voxel-gray-300';
    }

    // Checked states
    if (color === 'primary') {
      hoverColor = 'group-hover:bg-primary-100';
      if (checked) {
        boxColor = 'text-primary-600';
        checkColor = 'text-white';
      }
    }
    if (color === 'outlined') {
      hoverColor = 'group-hover:bg-voxel-gray-200/50';
      if (checked) {
        boxColor = 'text-slate-900';
        checkColor = 'text-slate-900';
      }
    }
    if (color === 'slate') {
      hoverColor = 'group-hover:bg-slate-100';
      if (checked) {
        boxColor = 'text-slate-600';
        checkColor = 'text-white';
      }
    }
    if (color === 'emerald') {
      hoverColor = 'group-hover:bg-emerald-600/10';
      if (checked) {
        boxColor = 'text-emerald-600/70';
        checkColor = 'text-white';
      }
    }
    if (color === 'indigo') {
      hoverColor = 'group-hover:bg-indigo-500/10';
      if (checked) {
        boxColor = 'text-indigo-500';
        checkColor = 'text-white';
      }
    }
    if (color === 'gray') {
      hoverColor = 'group-hover:bg-slate-400/10';
      if (checked) {
        boxColor = 'text-slate-300';
        checkColor = 'text-slate-900';
      }
    }


    return (
      <label
        {...labelProps}
        ref={ref}
        className={`
        flex flex-row flex-nowrap items-center gap-2
        ${!disabled && 'cursor-pointer'}
        group
        select-none
        ${widthClass}
      `}
        htmlFor={finalId}
        onClick={() => !disabled && !!onChange && onChange(!checked)}
      >
        <div
          className={`${size === 'sm' ? 'w-1' : ''}
            fa-layers fa-fw fa-1x
            bg-opacity-10
            rounded-full
            ${padding ? 'p-4' : 'p-2.5'}
            ${!disabled && hoverColor}
      `   }
        >
          <FontAwesomeIcon icon={boxIcon} size={'lg'} className={`${boxColor}`} />
          {!!checked && (
            <FontAwesomeIcon icon={checkIcon} size={'sm'} className={`${checkColor}`} />
          )}
        </div>
        <div>
          <div className={`text-sm whitespace-nowrap ${disabled && 'text-voxel-gray-400'}`}>
            {label}
          </div>
          {!!errorText?.length && (
            <VoxelText variant="form-caption" color="error" className="max-w-sm">
              {errorText}
            </VoxelText>
          )}
        </div>
      </label>
    );
  }
);

export default VoxelCheckbox;
